import type { Selection } from '@/components/admin-shared';
import { buildGlobalSelection } from '@/components/admin-shared';
import { globalApplicationOwnerLabel } from '@/lib/branding';
import { sortSopFlowSteps } from '../../shared/lib/sop-flow-validation.domain.js';
import { isSaveOnlyTerminalStep } from '../../shared/lib/sop-terminal-delivery.domain.js';
import {
  definitionKey,
  isCallListContactVariant,
  resolveRequireSuccessfulContact,
  type RequireSuccessfulContactDefinition,
} from '../../shared/lib/sop-step-variant.domain.js';
import {
  isMultipleChoiceStep,
  normalizeMultipleChoiceConfigForSave,
} from '../../shared/lib/multiple-choice-step.domain.js';

export { isCallListContactVariant };

export type SopOwnerType = 'GLOBAL' | 'DEALER' | 'SITE';

export type SopListItem = {
  id: number;
  name: string;
  description?: string | null;
  owner_type?: SopOwnerType | string | null;
  owner_id?: number | null;
  scope_type?: SopOwnerType | string | null;
  scope_id?: number | null;
  site_id?: number | null;
  is_active?: number | boolean;
  is_inheritable?: number | boolean;
  is_hidden_from_descendants?: number | boolean;
  code?: string | null;
  has_unpublished_draft?: boolean | number;
  draft_revision?: number | null;
  draft_updated_at?: string | null;
};

export type SopStepRow = {
  id: number;
  /** Authoring draft identity (`step:<id>` or `tmp:<uuid>`). */
  draft_step_id?: string;
  sop_id?: number;
  step_order: number;
  archetype: string | null;
  variant: string | null;
  title: string | null;
  prompt_text: string | null;
  config?: unknown;
  choice_positive_label?: string | null;
  choice_negative_label?: string | null;
  contact_reference_type?: string | null;
  contact_reference_value?: string | number | null;
  contact_reference_scope?: string | null;
  url_reference_type?: string | null;
  url_reference_value?: string | null;
  url?: string | null;
  next_step?: string | number | null;
  yes_next_step?: string | number | null;
  no_next_step?: string | number | null;
  include_in_report?: number | boolean;
  show_in_narrative_strip?: number | boolean;
  include_negative_in_report?: number | boolean;
  report_action_text?: string | null;
  positive_response_format?: string | null;
  negative_response_format?: string | null;
  camera_ref?: string | null;
  is_active?: number | boolean;
};

export type SopStepDefinition = {
  id: number;
  code?: string | null;
  archetype: string;
  variant?: string | null;
  label?: string | null;
  description?: string | null;
  usage_notes?: string | null;
  display_order?: number | null;
  linking_type?: string | null;
  yes_link_label?: string | null;
  no_link_label?: string | null;
  requires_successful_contact?: boolean | number | null;
  requires_contact_selection?: boolean | number | null;
  requires_camera_ref?: boolean | number | null;
  requires_url?: boolean | number | null;
  requires_prompt_text?: boolean | number | null;
};

export function parseStepConfigObject(config: unknown): Record<string, unknown> {
  if (config == null) return {};
  if (typeof config === 'string') {
    try {
      const parsed = JSON.parse(config);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  if (typeof config === 'object' && !Array.isArray(config)) {
    return { ...(config as Record<string, unknown>) };
  }
  return {};
}

export function findStepDefinitionForStep(
  step: Pick<SopStepRow, 'archetype' | 'variant'>,
  definitions: SopStepDefinition[],
): SopStepDefinition | undefined {
  const archetype = String(step.archetype || '').trim();
  const variant = step.variant != null ? String(step.variant).trim() : '';
  return definitions.find((row) => {
    if (row.archetype !== archetype) return false;
    if (!variant) return !row.variant;
    return row.variant === variant || row.code === variant || row.label === variant;
  });
}

export function getEffectiveRequireSuccessfulContact(
  step: Pick<SopStepRow, 'archetype' | 'variant' | 'config'>,
  definitions: SopStepDefinition[],
): boolean {
  const def = findStepDefinitionForStep(step, definitions);
  const definitionPayload: RequireSuccessfulContactDefinition = def
    ? { requires_successful_contact: def.requires_successful_contact }
    : null;
  return resolveRequireSuccessfulContact(step, definitionPayload);
}

/** Merge must-reach checkbox into step config (opt-out writes false; re-enable removes override). */
export function applyRequireSuccessfulContactConfig(
  step: Pick<SopStepRow, 'archetype' | 'variant' | 'config'>,
  definitions: SopStepDefinition[],
  enabled: boolean,
): Record<string, unknown> {
  const cfg = parseStepConfigObject(step.config);
  const def = findStepDefinitionForStep(step, definitions);
  const definitionDefault = def?.requires_successful_contact === true || def?.requires_successful_contact === 1;
  if (enabled === definitionDefault) {
    delete cfg.requireSuccessfulContact;
  } else {
    cfg.requireSuccessfulContact = enabled;
  }
  return cfg;
}

export type SopUsageResult = {
  scenarios: Array<{ id: number; name: string; owner_type?: string | null; owner_id?: number | null }>;
  sites: Array<{ id: number; name: string; parent_id?: number | null; parent_name?: string | null }>;
  assignments: Array<{ site_id: number; site_name: string; scenario_id: number; scenario_name: string }>;
};

const SOPS_INCLUDE_INHERITED_KEY = 'sops.includeInherited';
const SOPS_INCLUDE_DESCENDANTS_KEY = 'sops.includeDescendants';
const SOPS_DRAFTS_ONLY_KEY = 'sops.draftsOnly';

export type SopLibraryQueryOptions = {
  includeInherited: boolean;
  includeDescendants: boolean;
  draftsOnly: boolean;
};

export function getScopeOwnerRef(selection: Selection): { owner_type: SopOwnerType; owner_id: number | null } {
  if (selection.type === 'dealer') return { owner_type: 'DEALER', owner_id: selection.id };
  if (selection.type === 'site') return { owner_type: 'SITE', owner_id: selection.id };
  return { owner_type: 'GLOBAL', owner_id: null };
}

export function scopeQueryParams(
  selection: Selection,
  options: boolean | SopLibraryQueryOptions,
) {
  const ref = getScopeOwnerRef(selection);
  const queryOptions: SopLibraryQueryOptions = typeof options === 'boolean'
    ? { includeInherited: !options, includeDescendants: false, draftsOnly: false }
    : options;
  const params = new URLSearchParams();
  params.set('scopeType', ref.owner_type);
  if (ref.owner_type !== 'GLOBAL' && ref.owner_id != null) {
    params.set('scopeId', String(ref.owner_id));
  } else if (ref.owner_type === 'GLOBAL') {
    params.set('scopeId', '1');
  }
  if (!queryOptions.includeInherited) params.set('ownedOnly', '1');
  if (queryOptions.includeDescendants) params.set('includeDescendants', '1');
  if (queryOptions.draftsOnly) params.set('hasUnpublishedDraft', '1');
  return params;
}

/** Map SOP owner scope to Sites/SOPs admin tree selection for deep links and API fallback.
 *
 * Returns null when the scope type is recognized but the target entity (SITE/DEALER)
 * does not exist in the dealer tree. GLOBAL always resolves.
 */
export function selectionFromSopOwnerScope(
  ownerType: string | null | undefined,
  ownerId: number | null | undefined,
  dealers: DealerTreeScope[],
  applicationOwnerName?: string | null,
): Selection | null {
  const normalizedType = String(ownerType ?? '').trim().toUpperCase();
  if (normalizedType === 'GLOBAL') {
    return buildGlobalSelection(applicationOwnerName);
  }
  const rawId = ownerId != null ? Number(ownerId) : NaN;
  if (!Number.isFinite(rawId) || rawId <= 0) return null;
  const id = rawId;
  if (normalizedType === 'DEALER') {
    const dealer = dealers.find((row) => Number(row.id) === id);
    if (!dealer) return null;
    return {
      type: 'dealer',
      id,
      name: dealer.name?.trim() || `Dealer #${id}`,
    };
  }
  if (normalizedType === 'SITE') {
    for (const dealer of dealers) {
      const site = (dealer.sites || []).find((row) => Number(row.id) === id);
      if (site) {
        return { type: 'site', id, name: site.name?.trim() || `Site #${id}` };
      }
    }
    return null;
  }
  return null;
}

export function sopUrlScopeFromSelection(selection: Selection): {
  scopeType: SopOwnerType;
  scopeId?: number;
} {
  const ref = getScopeOwnerRef(selection);
  return {
    scopeType: ref.owner_type,
    scopeId: ref.owner_type === 'GLOBAL' ? 1 : ref.owner_id ?? undefined,
  };
}

export function isSopInheritedInScope(sop: SopListItem, selection: Selection): boolean {
  const scopeRef = getScopeOwnerRef(selection);
  const sopOwnerType = String(sop.owner_type || sop.scope_type || '').toUpperCase() as SopOwnerType;
  const sopOwnerId = sop.owner_id != null ? Number(sop.owner_id) : null;
  if (sopOwnerType !== scopeRef.owner_type) return true;
  if (scopeRef.owner_type === 'GLOBAL') return false;
  return Number(scopeRef.owner_id) !== Number(sopOwnerId);
}

/** True when the SOP is owned by a child scope relative to the current library selection. */
export function isSopDescendantInScope(sop: SopListItem, selection: Selection): boolean {
  const scopeRef = getScopeOwnerRef(selection);
  const sopOwnerType = String(sop.owner_type || sop.scope_type || '').toUpperCase() as SopOwnerType;
  if (scopeRef.owner_type === 'GLOBAL') {
    return sopOwnerType === 'DEALER' || sopOwnerType === 'SITE';
  }
  if (scopeRef.owner_type === 'DEALER') {
    return sopOwnerType === 'SITE';
  }
  return false;
}

export type DealerTreeScope = {
  id: number;
  name?: string | null;
  sites?: Array<{ id: number; name?: string | null }>;
};

export type SopOwnerDisplay = {
  label: string;
  ownerType: SopOwnerType | 'UNKNOWN';
  ownerId: number | null;
  /** Database ID and entity type for hover tooltips (not primary UI copy). */
  tooltip: string | null;
};

function ownerIdTooltip(ownerType: SopOwnerType | 'UNKNOWN', ownerId: number | null): string | null {
  if (ownerId == null || !Number.isFinite(ownerId)) return null;
  if (ownerType === 'GLOBAL') return `Application owner scope ID: ${ownerId}`;
  if (ownerType === 'DEALER') return `Dealer ID: ${ownerId}`;
  if (ownerType === 'SITE') return `Site ID: ${ownerId}`;
  return `Owner ID: ${ownerId}`;
}

/** Human-readable SOP owner label; pass `dealers` from the scope tree to resolve site/dealer names. */
export function resolveSopOwnerDisplay(
  sop: SopListItem,
  dealers?: DealerTreeScope[],
  applicationOwnerName?: string | null,
): SopOwnerDisplay {
  const ownerType = String(sop.owner_type || sop.scope_type || '').toUpperCase() as SopOwnerType | 'UNKNOWN';
  const ownerId = sop.owner_id != null ? Number(sop.owner_id) : null;

  if (ownerType === 'GLOBAL') {
    return { label: globalApplicationOwnerLabel(applicationOwnerName), ownerType: 'GLOBAL', ownerId, tooltip: ownerIdTooltip('GLOBAL', ownerId) };
  }

  if (ownerType === 'DEALER') {
    const dealer = dealers?.find((row) => Number(row.id) === Number(ownerId));
    const label = dealer?.name?.trim() || (ownerId ? `Dealer #${ownerId}` : 'Dealer');
    return { label, ownerType: 'DEALER', ownerId, tooltip: ownerIdTooltip('DEALER', ownerId) };
  }

  if (ownerType === 'SITE') {
    if (dealers) {
      for (const dealer of dealers) {
        const site = (dealer.sites || []).find((row) => Number(row.id) === Number(ownerId));
        if (site) {
          const label = site.name?.trim() || `Site #${site.id}`;
          return { label, ownerType: 'SITE', ownerId, tooltip: ownerIdTooltip('SITE', ownerId) };
        }
      }
    }
    return {
      label: ownerId ? `Site #${ownerId}` : 'Site',
      ownerType: 'SITE',
      ownerId,
      tooltip: ownerIdTooltip('SITE', ownerId),
    };
  }

  return {
    label: ownerType || 'Unknown',
    ownerType: 'UNKNOWN',
    ownerId,
    tooltip: ownerId != null ? `Owner ID: ${ownerId}` : null,
  };
}

export function describeSopOwner(
  sop: SopListItem,
  dealers?: DealerTreeScope[],
  applicationOwnerName?: string | null,
): string {
  return resolveSopOwnerDisplay(sop, dealers, applicationOwnerName).label;
}

export function getTerminalDisplayTitle(step: Pick<SopStepRow, 'archetype' | 'variant' | 'config'>): string {
  return isSaveOnlyTerminalStep(step) ? 'Save Report Only' : 'End SOP';
}

export function sortSopSteps(steps: SopStepRow[]): SopStepRow[] {
  return sortSopFlowSteps(steps);
}

/** 1-based position in the sorted timeline — canonical admin "Step N" label. */
export function buildSopStepDisplayNumberMap(steps: SopStepRow[]): Map<number, number> {
  const map = new Map<number, number>();
  sortSopSteps(steps).forEach((step, index) => {
    const id = Number(step.id);
    if (Number.isFinite(id) && id > 0) map.set(id, index + 1);
  });
  return map;
}

/** Map lookup with step_order fallback when id is missing or not indexed (draft/invalid ids). */
export function resolveSopStepDisplayNumber(
  step: Pick<SopStepRow, 'id' | 'step_order'>,
  displayNumbers: Map<number, number>,
): number {
  const stepId = Number(step.id);
  return displayNumbers.get(stepId) ?? (Number(step.step_order) || 0);
}

export function getSopStepDisplayNumber(steps: SopStepRow[], stepId: number): number {
  const step = steps.find((row) => Number(row.id) === Number(stepId));
  if (!step) return 0;
  return resolveSopStepDisplayNumber(step, buildSopStepDisplayNumberMap(steps));
}

/** 1-based step_order for inserting at display index (0 = before step 1). */
export function stepOrderForInsertAtIndex(steps: SopStepRow[], insertAtIndex: number): number {
  const sorted = sortSopSteps(steps);
  if (insertAtIndex <= 0) return 1;
  if (insertAtIndex >= sorted.length) {
    const last = sorted[sorted.length - 1];
    return (Number(last?.step_order) || sorted.length) + 1;
  }
  const target = sorted[insertAtIndex];
  return Number(target.step_order) || insertAtIndex + 1;
}

/** Human-readable insert position for the add-step dialog. */
export function insertStepLabel(steps: SopStepRow[], insertAtIndex: number | null): string | null {
  if (insertAtIndex == null) return null;
  const sorted = sortSopSteps(steps);
  if (!sorted.length || insertAtIndex <= 0) return 'Insert before step 1';
  if (insertAtIndex >= sorted.length) {
    return `Insert after step ${sorted.length}`;
  }
  return `Insert before step ${insertAtIndex + 1}`;
}

/** First terminal step display index, or null when none. */
export function firstTerminalDisplayIndex(steps: SopStepRow[]): number | null {
  const sorted = sortSopSteps(steps);
  const index = sorted.findIndex((step) => String(step.archetype || '').toUpperCase() === 'TERMINAL');
  return index >= 0 ? index : null;
}

export function getStepTypeLabel(
  step: Pick<SopStepRow, 'archetype' | 'variant'>,
  definitions: SopStepDefinition[],
): string {
  const archetype = String(step.archetype || '').trim();
  const variant = step.variant != null ? String(step.variant).trim() : '';
  const match = definitions.find((row) => {
    if (row.archetype !== archetype) return false;
    if (!variant) return !row.variant;
    return row.variant === variant || row.code === variant || row.label === variant;
  });
  if (match?.label) return match.label;
  if (variant) return `${archetype} · ${variant}`;
  return archetype || 'Step';
}

export type ContactReferenceOption = { value: string; label: string };

export type ContactReferenceOptionsContext = {
  /** SOP owner scope — dealer/global templates prefer semantic runtime-site refs. */
  sopOwnerType?: string | null;
};

function ownerTypeLabel(sopOwner: string): string {
  if (sopOwner === 'GLOBAL') return 'Use a global-owned contact list (not site-owned)';
  if (sopOwner === 'DEALER') return 'Use a dealer- or global-owned list (site-owned blocked)';
  return 'Override site contact list';
}

/** Variant-aware contact reference types (legacy sops.js parity). */
export function getContactReferenceOptions(
  variant: string | null | undefined,
  currentRefType?: string | null,
  context?: ContactReferenceOptionsContext,
): ContactReferenceOption[] {
  const v = variant != null ? String(variant).trim() : '';
  const current = currentRefType != null ? String(currentRefType).trim() : '';
  const sopOwner = String(context?.sopOwnerType ?? '').trim().toUpperCase();
  const portableTemplate = sopOwner === 'DEALER' || sopOwner === 'GLOBAL';
  const isListType = current === 'SITE_DEFAULT_LIST' || current === 'EXPLICIT_LIST';
  const options: ContactReferenceOption[] = [];

  if (v === 'call_list' || v === 'call_owner_list' || isListType) {
    options.push({
      value: 'SITE_DEFAULT_LIST',
      label: portableTemplate ? "Use runtime site's contact list" : 'Use site contact list',
    });
    options.push({
      value: 'EXPLICIT_LIST',
      label: portableTemplate
        ? ownerTypeLabel(sopOwner)
        : 'Override site contact list',
    });
  } else if (v === 'call_group' || v === 'call') {
    options.push({
      value: 'SITE_DEFAULT_LIST',
      label: portableTemplate ? "Use runtime site's contact list" : 'Use site contact list',
    });
    options.push({
      value: 'EXPLICIT_LIST',
      label: portableTemplate
        ? ownerTypeLabel(sopOwner)
        : 'Override site contact list',
    });
  } else if (v === 'emergency_police') {
    options.push({ value: 'SITE_POLICE', label: "Use site's police contact" });
  } else if (v === 'emergency_fire') {
    options.push({ value: 'SITE_FIRE', label: "Use site's fire department contact" });
  } else if (v === 'emergency_security') {
    options.push({ value: 'SITE_SECURITY', label: "Use site's Security Callout Group (default)" });
    options.push({ value: 'SITE_SECURITY_CONTACT', label: "Use site's Security contact" });
  } else {
    options.push({
      value: 'SITE_DEFAULT_LIST',
      label: portableTemplate ? "Use runtime site's contact list" : 'Use site contact list',
    });
    options.push({
      value: 'EXPLICIT_LIST',
      label: portableTemplate
        ? ownerTypeLabel(sopOwner)
        : 'Override site contact list',
    });
    options.push({ value: 'SITE_POLICE', label: "Use site's police contact" });
    options.push({ value: 'SITE_FIRE', label: "Use site's fire department contact" });
    options.push({ value: 'SITE_SECURITY', label: "Use site's Security Callout Group" });
  }

  const legacyOnly: ContactReferenceOption[] = [
    { value: 'SITE_DEFAULT_GROUP', label: 'Site default group (legacy)' },
    { value: 'EXPLICIT_GROUP', label: 'Explicit group (legacy)' },
    { value: 'EXPLICIT_CONTACT', label: 'Explicit contact (legacy)' },
    { value: 'SITE_ALL_CONTACTS', label: 'All site contacts (legacy)' },
  ];
  for (const leg of legacyOnly) {
    if (current === leg.value && !options.some((o) => o.value === leg.value)) {
      options.push(leg);
    }
  }
  return options;
}

function parseBooleanFlag(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
}

export function buildStepSavePayload(step: SopStepRow, updates: Partial<SopStepRow>): Record<string, unknown> {
  const archetype = updates.archetype !== undefined ? String(updates.archetype) : (step.archetype || 'PROMPT');
  const variant = updates.variant !== undefined ? updates.variant : (step.variant || null);
  let contactRefType =
    updates.contact_reference_type !== undefined ? updates.contact_reference_type : (step.contact_reference_type || null);
  if (!contactRefType) {
    if (variant === 'emergency_police') contactRefType = 'SITE_POLICE';
    else if (variant === 'emergency_fire') contactRefType = 'SITE_FIRE';
    else if (variant === 'emergency_security') contactRefType = 'SITE_SECURITY';
  }

  const body: Record<string, unknown> = {
    step_order: Number(updates.step_order ?? step.step_order) || 0,
    archetype,
    variant,
    choice_positive_label:
      updates.choice_positive_label !== undefined ? updates.choice_positive_label : (step.choice_positive_label || null),
    choice_negative_label:
      updates.choice_negative_label !== undefined ? updates.choice_negative_label : (step.choice_negative_label || null),
    title:
      archetype === 'TERMINAL'
        ? getTerminalDisplayTitle({ archetype, variant, config: updates.config ?? step.config })
        : updates.title !== undefined
          ? updates.title
          : step.title,
    prompt_text: updates.prompt_text !== undefined ? updates.prompt_text : step.prompt_text,
    contact_reference_type: contactRefType,
    contact_reference_value:
      contactRefType && String(contactRefType).startsWith('EXPLICIT_')
        ? (updates.contact_reference_value !== undefined
          ? updates.contact_reference_value
          : (step.contact_reference_value || null))
        : null,
    contact_reference_scope:
      contactRefType && String(contactRefType).startsWith('EXPLICIT_')
        ? (updates.contact_reference_scope !== undefined ? updates.contact_reference_scope : (step.contact_reference_scope || 'GLOBAL'))
        : 'SITE',
    url_reference_type: archetype === 'RESOURCE_VIEW' ? 'EXPLICIT' : (step.url_reference_type || null),
    url_reference_value:
      archetype === 'RESOURCE_VIEW'
        ? (updates.url_reference_value ?? updates.url ?? step.url_reference_value ?? step.url ?? null)
        : (step.url_reference_value || null),
    camera_ref: updates.camera_ref !== undefined ? updates.camera_ref : step.camera_ref ?? null,
    url: updates.url !== undefined ? updates.url : (step.url || null),
    next_step: updates.next_step !== undefined ? updates.next_step : (step.next_step || null),
    yes_next_step: updates.yes_next_step !== undefined ? updates.yes_next_step : (step.yes_next_step || null),
    no_next_step: updates.no_next_step !== undefined ? updates.no_next_step : (step.no_next_step || null),
    positive_response_format:
      updates.positive_response_format !== undefined
        ? updates.positive_response_format
        : (step.positive_response_format ?? null),
    negative_response_format:
      updates.negative_response_format !== undefined
        ? updates.negative_response_format
        : (step.negative_response_format ?? null),
    include_negative_in_report:
      updates.include_negative_in_report !== undefined
        ? parseBooleanFlag(updates.include_negative_in_report)
        : parseBooleanFlag(step.include_negative_in_report),
    include_in_report:
      updates.include_in_report !== undefined
        ? parseBooleanFlag(updates.include_in_report)
        : parseBooleanFlag(step.include_in_report !== undefined ? step.include_in_report : true),
    show_in_narrative_strip:
      updates.show_in_narrative_strip !== undefined
        ? parseBooleanFlag(updates.show_in_narrative_strip)
        : parseBooleanFlag(step.show_in_narrative_strip),
    report_action_text: updates.report_action_text !== undefined ? updates.report_action_text : (step.report_action_text || null),
    is_active: step.is_active ? 1 : 0,
  };

  if (updates.config !== undefined) {
    body.config = isMultipleChoiceStep(archetype)
      ? normalizeMultipleChoiceConfigForSave(updates.config)
      : updates.config;
  } else if (isMultipleChoiceStep(archetype) && step.config != null) {
    body.config = normalizeMultipleChoiceConfigForSave(step.config);
  } else if (archetype === 'SPECIAL_ACTION' && variant === 'custom_list' && step.config != null) {
    body.config = typeof step.config === 'string'
      ? (() => {
          try {
            return JSON.parse(step.config);
          } catch {
            return {};
          }
        })()
      : step.config;
  } else if (archetype === 'RESOURCE_VIEW') {
    body.config = parseStepConfigObject(updates.config !== undefined ? updates.config : step.config);
  }

  if (archetype === 'BINARY_CHOICE') {
    if (!body.choice_positive_label) body.choice_positive_label = step.choice_positive_label || 'Yes';
    if (!body.choice_negative_label) body.choice_negative_label = step.choice_negative_label || 'No';
  }

  return body;
}

export function loadIncludeInheritedPreference(userEmail: string | null): boolean | null {
  try {
    const userKey = userEmail
      ? `${SOPS_INCLUDE_INHERITED_KEY}:${String(userEmail).toLowerCase()}`
      : SOPS_INCLUDE_INHERITED_KEY;
    const userValue = localStorage.getItem(userKey);
    if (userValue === '1' || userValue === '0') return userValue === '1';
    const fallbackValue = localStorage.getItem(SOPS_INCLUDE_INHERITED_KEY);
    if (fallbackValue === '1' || fallbackValue === '0') return fallbackValue === '1';
  } catch {
    /* ignore */
  }
  return null;
}

export function saveIncludeInheritedPreference(includeInherited: boolean, userEmail: string | null) {
  try {
    const key = userEmail
      ? `${SOPS_INCLUDE_INHERITED_KEY}:${String(userEmail).toLowerCase()}`
      : SOPS_INCLUDE_INHERITED_KEY;
    localStorage.setItem(key, includeInherited ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function loadBooleanPreference(baseKey: string, userEmail: string | null): boolean | null {
  try {
    const userKey = userEmail ? `${baseKey}:${String(userEmail).toLowerCase()}` : baseKey;
    const userValue = localStorage.getItem(userKey);
    if (userValue === '1' || userValue === '0') return userValue === '1';
    const fallbackValue = localStorage.getItem(baseKey);
    if (fallbackValue === '1' || fallbackValue === '0') return fallbackValue === '1';
  } catch {
    /* ignore */
  }
  return null;
}

function saveBooleanPreference(baseKey: string, value: boolean, userEmail: string | null) {
  try {
    const key = userEmail ? `${baseKey}:${String(userEmail).toLowerCase()}` : baseKey;
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function loadIncludeDescendantsPreference(userEmail: string | null): boolean | null {
  return loadBooleanPreference(SOPS_INCLUDE_DESCENDANTS_KEY, userEmail);
}

export function saveIncludeDescendantsPreference(includeDescendants: boolean, userEmail: string | null) {
  saveBooleanPreference(SOPS_INCLUDE_DESCENDANTS_KEY, includeDescendants, userEmail);
}

export function loadDraftsOnlyPreference(userEmail: string | null): boolean | null {
  return loadBooleanPreference(SOPS_DRAFTS_ONLY_KEY, userEmail);
}

export function saveDraftsOnlyPreference(draftsOnly: boolean, userEmail: string | null) {
  saveBooleanPreference(SOPS_DRAFTS_ONLY_KEY, draftsOnly, userEmail);
}

export function sopHasUnpublishedDraft(sop: SopListItem): boolean {
  return sop.has_unpublished_draft === true || sop.has_unpublished_draft === 1;
}

/** Parse comma/newline-separated option labels (legacy sops.js custom_list). */
export function parseCsvLikeInputToOptions(raw: string): string[] {
  const text = String(raw || '').trim();
  if (!text) return [];
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      if (inQ && text[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQ = !inQ;
      }
    } else if (!inQ && (ch === '\n' || ch === '\r')) {
      if (cur.trim()) out.push(cur.trim());
      cur = '';
    } else if (!inQ && ch === ',') {
      if (cur.trim()) out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur.trim()) out.push(cur.trim());
  return [...new Set(out.map((s) => s.trim()).filter(Boolean))];
}

export function dedupeOptionLabels(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const label = String(value || '').trim();
    if (!label) continue;
    const key = label.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

export function resolveSimulatorSiteId(
  sop: SopListItem,
  selection: Selection,
  usage: SopUsageResult | null | undefined,
): number | null {
  if (selection.type === 'site' && selection.id) return Number(selection.id);
  if (sop.site_id != null && Number(sop.site_id) > 0) return Number(sop.site_id);
  const first = usage?.assignments?.[0]?.site_id;
  if (first != null && Number(first) > 0) return Number(first);
  return null;
}

export function canTestSopInSimulator(user: { permissions?: string[]; isAdmin?: boolean } | null | undefined): boolean {
  if (!user) return false;
  if (user.isAdmin) return true;
  const permissions = user.permissions ?? [];
  return permissions.includes('pages.operator_simulator.access') && permissions.includes('sops.execute');
}

export function canManageSops(user: { isAdmin?: boolean; permissions?: string[] } | null | undefined): boolean {
  if (!user) return false;
  if (user.isAdmin) return true;
  const permissions = user.permissions ?? [];
  return permissions.includes('sops.create') || permissions.includes('sops.edit') || permissions.includes('sops.delete');
}
